const GENDER_CUSTOMER_PREFIX = 'gender:';

/** Mirrors MDiForm `gender-purchase-eligibility.ts` for PDP Start Flow gating. */
function customerTagsToGenderRole(tags) {
  const lowered = tags.map((t) => t.trim().toLowerCase()).filter(Boolean);
  if (lowered.includes(`${GENDER_CUSTOMER_PREFIX}all`)) return 'all';
  if (lowered.includes(`${GENDER_CUSTOMER_PREFIX}female`)) return 'female';
  if (lowered.includes(`${GENDER_CUSTOMER_PREFIX}male`)) return 'male';
  return 'all';
}

function normalizeGenderMetafieldValue(raw) {
  let r = (raw ?? '').trim();
  if (!r) return '';
  if (r.startsWith('"') && r.endsWith('"')) {
    try {
      const parsed = JSON.parse(r);
      if (typeof parsed === 'string') r = parsed;
      else r = r.slice(1, -1);
    } catch {
      r = r.slice(1, -1);
    }
  }
  return r.trim().toLowerCase();
}

function genderAudienceFromProductMetafield(metafieldValue) {
  const raw = normalizeGenderMetafieldValue(metafieldValue);
  if (!raw || raw === '0') return 'all';
  if (raw === 'female' || raw === 'femenino' || raw === 'for her' || raw === 'for-her') return 'female';
  if (raw === 'male' || raw === 'masculino' || raw === 'for him' || raw === 'for-him') return 'male';
  if (raw === 'all' || raw === 'unisex' || raw === 'both') return 'all';
  return 'all';
}

function canCustomerPurchaseProduct(customerRole, productAudience) {
  if (customerRole === 'all' || productAudience === 'all') return true;
  return customerRole === productAudience;
}

/**
 * Logged-in customer on a gender-specific PDP whose `gender:*` tag conflicts with product audience.
 */
function isLoggedInGenderPurchaseBlocked(app) {
  if (app.dataset.customerLoggedIn !== 'true') return false;
  const csv = app.dataset.customerTags || '';
  const tags = csv.split(',').map((t) => t.trim()).filter(Boolean);
  const customerRole = customerTagsToGenderRole(tags);
  const productAudience = genderAudienceFromProductMetafield(app.dataset.productGender || '');
  return !canCustomerPurchaseProduct(customerRole, productAudience);
}

function removeExistingGenderPdpChrome() {
  document.querySelectorAll('[data-gender-warning-pdp], [data-mdi-pdp-quiz-slot]').forEach((el) => el.remove());
}

function buildGenderMismatchWarning(message) {
  const wrap = document.createElement('div');
  wrap.setAttribute('data-gender-warning-pdp', '');
  wrap.setAttribute('role', 'alert');

  const iconWrap = document.createElement('span');
  iconWrap.setAttribute('aria-hidden', 'true');
  iconWrap.innerHTML = `
<svg xmlns="http://www.w3.org/2000/svg" width="18" height="16" viewBox="0 0 18 16" fill="none" aria-hidden="true">
<path d="M8.71875 1.86328L1.75781 13.2891C1.6875 13.3945 1.6875 13.4648 1.6875 13.5703C1.6875 13.8516 1.89844 14.0625 2.17969 14.0625H15.7852C16.0664 14.0625 16.3125 13.8516 16.3125 13.5703C16.3125 13.4648 16.2773 13.3945 16.207 13.2891L9.24609 1.86328C9.21094 1.75781 9.10547 1.6875 9 1.6875C8.85938 1.6875 8.78906 1.75781 8.71875 1.86328ZM7.27734 0.984375C7.62891 0.386719 8.29688 0 9 0C9.66797 0 10.3359 0.386719 10.6875 0.984375L17.6484 12.4102C17.8594 12.7617 18 13.1484 18 13.5703C18 14.7656 17.0156 15.75 15.7852 15.75H2.17969C0.984375 15.75 0 14.7656 0 13.5703C0 13.1484 0.105469 12.7617 0.316406 12.4102L7.27734 0.984375ZM10.125 11.8125C10.125 12.4453 9.59766 12.9375 9 12.9375C8.36719 12.9375 7.875 12.4453 7.875 11.8125C7.875 11.2148 8.36719 10.6875 9 10.6875C9.59766 10.6875 10.125 11.2148 10.125 11.8125ZM9.84375 5.34375V8.71875C9.84375 9.21094 9.45703 9.5625 9 9.5625C8.50781 9.5625 8.15625 9.21094 8.15625 8.71875V5.34375C8.15625 4.88672 8.50781 4.5 9 4.5C9.45703 4.5 9.84375 4.88672 9.84375 5.34375Z" fill="currentColor"/>
</svg>`;

  const text = document.createElement('div');
  text.textContent = message;

  wrap.appendChild(iconWrap);
  wrap.appendChild(text);
  return wrap;
}

const initMdiModal = (app = document.querySelector('[data-app-layer-portal="form-portal"]')) => {
  const productForm = document.querySelector('form[data-type="add-to-cart-form"]');

  // Add to cart form not found. Do nothing.
  if(!productForm) {
    return;
  }

  const productId = window.meta.product.id;
  const cookieName = 'Vs_MDi_log_' + productId;
  const progressCookieName = 'Vs_MDi_log_' + productId; // Same as cookieName - the progress cookie
  const DATE_PADDING_LENGTH = 2;
  const TAKE_QUIZ_SELECTOR = '[data-take-quiz-el]';
  const BUTTON_CLASSES = ['btn', 'btn-primary', 'w-full'];
  /**
   * Does cookie with specified name exist?
   * @return boolean
   */
  const checkIfCookieExists = () => {

    // Check if cookie "Vs_MDi_log_" + productId + " exists
    const cookieValue = document.cookie.split(';').find((row) => row.trim().startsWith(cookieName + '='));
    const cookie = cookieValue;
    return !!cookieValue;
  };

  /**
   * Does progress cookie exist with meaningful progress?
   * @return boolean
   */
  const checkIfProgressCookieExists = () => {
    const cookieValue = document.cookie.split(';').find((row) => row.trim().startsWith(progressCookieName + '='));

    if (!cookieValue) {
      return false;
    }

    try {
      // Extract cookie value after "cookieName="
      const cookieData = cookieValue.split('=')[1];
      const parsedCookie = JSON.parse(decodeURIComponent(cookieData));

      // Check if user has progressed beyond welcome screen
      // If they're still on 'welcome', they haven't really started
      const hasRealProgress = parsedCookie.step && parsedCookie.step !== 'welcome';

      return hasRealProgress;
    } catch {
      return false;
    }
  };

  const checkIfCustomerHasApprovalForProduct = (foundEntry) => {
    if (!foundEntry) return false;

    return Date.parse(foundEntry.expiry_date) >= Date.now();
  };

  const voucherModal = document.querySelector('voucher-modal');

  const questionnaireJson = app.dataset;
  questionnaireJson.questionnaireId = questionnaireJson.activeLocale == 'es' ? questionnaireJson.esQuestions : questionnaireJson.enQuestions;

  const cookieExists = checkIfCookieExists();

  const addPropertyToProductForm = (key, value) => {
    const newInput = document.createElement('input');
    newInput.name=`properties[${key}]`;
    newInput.value = value;
    newInput.type = 'hidden';
    productForm.appendChild(newInput);
  };

  const switchToProductForm = () => {
    productForm.classList.remove('hidden');
    app.classList.add('hidden');
    document.body.classList.remove('overflow-hidden');
  };

  const addPropertiesToForm = (voucherId, caseId, stateSurcharge) => {
    const d = new Date();

    addPropertyToProductForm('_voucher_id', voucherId);
    addPropertyToProductForm('_case_id', caseId);
    addPropertyToProductForm('_state_surcharge', stateSurcharge);
    addPropertyToProductForm('_accept_terms', d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-DATE_PADDING_LENGTH) + '-' + ('0' + d.getDate()).slice(-DATE_PADDING_LENGTH));

    switchToProductForm();
  };
    
  const skipQuestionnaire = (foundEntry) => {
    addPropertiesToForm(foundEntry.voucher_id, foundEntry.case_id);
  };

  const checkIfActiveSubscriber = () => {
    const customerTags = app.dataset.customerTags || '';
    const productTitle = app.dataset.productTitle || '';

    if (!customerTags || !productTitle) return false;

    const tags = customerTags.split(',').map(tag => tag.trim());
    const activeTag = `Active_${productTitle.trim()}`;

    return tags.includes(activeTag);
  };

  /**
   * Check if user has progress via backend API. MDiForm's `lib/flow-steps.ts`
   * (isPastAccountCreationStep) only persists /apps/rx/progress/update calls from shipping-info
   * onward now — so a real saved step returned here inherently means the customer registered and
   * got at least that far; trust whatever the backend actually says rather than guessing locally.
   * @return {Promise<boolean>}
   */
  const checkIfUserHasProgress = async () => {
    // First check local cookie for backward compatibility
    const hasLocalProgress = checkIfProgressCookieExists();
    if (hasLocalProgress) {
      return true;
    }

    // Check if user is logged in with required credentials
    const customerEmail = app.dataset.customerEmail;
    const isLoggedIn = app.dataset.customerLoggedIn === 'true';

    if (!isLoggedIn || !customerEmail) {
      return false;
    }

    // Get token from cookie if available
    const tokenMatch = document.cookie.match(/mdi_customer_token=([^;]+)/);
    const customerAccessToken = tokenMatch ? tokenMatch[1] : null;

    // The backend matches progress on the token itself, not on email alone — without one,
    // this call is guaranteed to find nothing, so skip it rather than firing it pointlessly.
    if (!customerAccessToken) {
      return false;
    }

    // Check backend for progress
    try {
      const response = await fetch('/apps/rx/progress/get', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_url: window.location.href.split('?')[0], // eslint-disable-line camelcase
          product_name: app.dataset.productTitle, // eslint-disable-line camelcase
          email: customerEmail,
          customer_access_token: customerAccessToken || null, // eslint-disable-line camelcase
        }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      const hasProgress = data && data.progress && data.progress.step && data.progress.step !== 'welcome';

      return hasProgress;
    } catch {
      return false;
    }
  };

  /**
   * Check if user is at checkout step with items in cart
   * @return {Promise<{isAtCheckout: boolean, checkoutCartData: object|null}>}
   */
  const checkIfUserAtCheckout = async () => {
    // Always on, unconditionally — recovering an abandoned checkout is real revenue recovery,
    // not general mid-flow resume.
    const customerEmail = app.dataset.customerEmail;
    const isLoggedIn = app.dataset.customerLoggedIn === 'true';

    if (!isLoggedIn || !customerEmail) {
      return { isAtCheckout: false, checkoutCartData: null };
    }

    // Get token from cookie if available
    const tokenMatch = document.cookie.match(/mdi_customer_token=([^;]+)/);
    const customerAccessToken = tokenMatch ? tokenMatch[1] : null;

    // The backend matches progress on the token itself, not on email alone — without one,
    // this call is guaranteed to find nothing, so skip it rather than firing it pointlessly.
    if (!customerAccessToken) {
      return { isAtCheckout: false, checkoutCartData: null };
    }

    try {
      const response = await fetch('/apps/rx/progress/get', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_url: window.location.href.split('?')[0], // eslint-disable-line camelcase
          product_name: app.dataset.productTitle, // eslint-disable-line camelcase
          email: customerEmail,
          customer_access_token: customerAccessToken || null, // eslint-disable-line camelcase
        }),
      });

      if (!response.ok) {
        return { isAtCheckout: false, checkoutCartData: null };
      }

      const data = await response.json();
      const isAtCheckout = data?.progress?.step === 'checkout' && data?.progress?.checkoutCartData;

      return {
        isAtCheckout,
        checkoutCartData: isAtCheckout ? data.progress.checkoutCartData : null
      };
    } catch {
      return { isAtCheckout: false, checkoutCartData: null };
    }
  };

  const appendPdpQuizSlot = (buttonEl, blockGender) => {
    const slot = document.createElement('div');
    slot.setAttribute('data-mdi-pdp-quiz-slot', '');
    if (blockGender) {
      const msg =
        app.dataset.genderMismatchMessage ||
        'This product is not available for your account profile. Please choose the product that matches your profile or contact support.';
      slot.appendChild(buildGenderMismatchWarning(msg));
      buttonEl.disabled = true;
      buttonEl.setAttribute('aria-disabled', 'true');
    }
    slot.appendChild(buttonEl);
    productForm.after(slot);
  };

  const switchToQuizStart = async () => {
    removeExistingGenderPdpChrome();
    // Hide existing product form
    productForm.classList.add('hidden');

    const isActiveSubscriber = checkIfActiveSubscriber();
    const locale = app.dataset.activeLocale || 'en';
    const genderBlocked = isLoggedInGenderPurchaseBlocked(app);

    if (isActiveSubscriber) {
      // Customer has active subscription - show "Go to Account" link
      const buttonText = locale === 'es' ? 'Ir a la cuenta' : 'Go to Account';
      const accountUrl = locale === 'en' ? '/en/account' : '/account';

      const newEl = document.createElement('a');
      newEl.href = accountUrl;
      newEl.classList.add(...BUTTON_CLASSES, 'block', 'w-full', 'text-center');
      newEl.appendChild(document.createTextNode(buttonText));
      productForm.after(newEl);
    } else {
      // Check if user is at checkout with cart data
      const { isAtCheckout } = await checkIfUserAtCheckout();

      if (isAtCheckout) {
        // User is at checkout - show "Go to Payment" button
        const buttonText = locale === 'es' ? 'Ir al pago' : 'Go to Payment';

        const newEl = document.createElement('button');
        newEl.type = 'button';
        newEl.dataset.takeQuizEl = true;
        newEl.classList.add(...BUTTON_CLASSES);
        // Add test ID based on product
        const productTitle = (app.dataset.productTitle || '').toLowerCase();
        if (productTitle.includes('alpha')) {
          newEl.dataset.testid = 'get_started_men';
        } else if (productTitle.includes('deseo')) {
          newEl.dataset.testid = 'get_started_women';
        }
        newEl.appendChild(document.createTextNode(buttonText));
        appendPdpQuizSlot(newEl, genderBlocked);
      } else {
        // Regular flow - check if user has progress (via API or local cookie)
        const hasProgress = await checkIfUserHasProgress();
        const buttonText = hasProgress ? app.dataset.altText : app.dataset.startText;

        // add "start" or "continue" button:
        const newEl = document.createElement('button');
        newEl.type = 'button';
        newEl.dataset.takeQuizEl = true;
        newEl.classList.add(...BUTTON_CLASSES);
        // Add test ID based on product
        const productTitle = (app.dataset.productTitle || '').toLowerCase();
        if (productTitle.includes('alpha')) {
          newEl.dataset.testid = 'get_started_men';
        } else if (productTitle.includes('deseo')) {
          newEl.dataset.testid = 'get_started_women';
        }
        newEl.appendChild(document.createTextNode(buttonText));
        appendPdpQuizSlot(newEl, genderBlocked);
      }
    }
  };


  const toggleStartButtonText = () => {
    if(cookieExists) {
      // If the element has an altText data attribute, use that as the text content
      const takeQuizEls = document.querySelectorAll(TAKE_QUIZ_SELECTOR);
      takeQuizEls.forEach((el) => el.dataset.hasOwnProperty('altText') ? el.textContent = el.dataset.altText : null);
    }
  };

  const showModal = () => {
    // Always get fresh reference to app element (in case DOM was recreated)
    const currentApp = document.querySelector('[data-app-layer-portal="form-portal"]');

    if (!currentApp) {
      return;
    }

    // Pause any playing video on the page
    const videoWrapper = document.querySelector('[data-custom-video-wrapper]');
    if (videoWrapper) {
      const video = videoWrapper.querySelector('video');
      if (video && !video.paused) {
        video.pause();
      }
    }

    currentApp.classList.remove('hidden');
    document.body.classList.add('overflow-hidden');
  };

  const createEventListener = () => {
    const takeQuizEls = document.querySelectorAll(TAKE_QUIZ_SELECTOR);

    takeQuizEls.forEach((quizEl) => {
      quizEl.addEventListener('click', () => {
        if (quizEl.disabled) return;
        showModal();
      });
    });

    // Also use event delegation on document for robustness
    // This ensures clicks work even if button is recreated
    document.addEventListener('click', (e) => {
      const quizEl = e.target && e.target.closest ? e.target.closest(TAKE_QUIZ_SELECTOR) : null;
      if (!quizEl || quizEl.disabled) return;
      showModal();
    });
  };

  const initForm = async () => {
    if (questionnaireJson.questionnaireId) {
      const foundEntry = JSON.parse(questionnaireJson.mdiData).find(
        (entry) => entry.product_id === `gid://shopify/Product/${questionnaireJson.productId}`
      );

      const customerHasApproval =  checkIfCustomerHasApprovalForProduct(foundEntry);

      if (customerHasApproval) {
        skipQuestionnaire();
      } else {
        await switchToQuizStart(); // Wait for button to be created
        createEventListener(); // Then attach event listener
        toggleStartButtonText();
      }
    }
  };

  initForm();


};

export default initMdiModal;
